// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullChampionship,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';

// Global Components
import * as React from 'react';

// Helpers
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  getRegionByRegionSeasonId,
  getRegionSeasonByRegionSeasonId,
} from 'common.helpers/dist/browser';
import {
  getLocalItemBasePath,
} from '../../../../helpers';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import EmptyList from '../../../common/EmptyList';
import ChampionshipsList from '../../../common/ChampionshipsList';
import NoData from '../../../common/NoData';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  RegionSeasonId: string;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  activeRegionSeasonId: string;
  data: Array<$FullChampionship>;
  loading: boolean;
};

class ChampionshipsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      activeRegionSeasonId: props.RegionSeasonId,
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      RegionSeasonId,
    } = this.props;

    if (isDiff(
      prevProps.RegionSeasonId,
      RegionSeasonId,
    )) {
      this.setActiveRegionSeason({
        RegionSeasonId,
      });
    }
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      activeRegionSeasonId,
    } = this.state;

    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getChampionshipsChampionshipsSectionData({
        RegionSeasonId: activeRegionSeasonId,
      });

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      activeRegionSeasonId,
    } = this.state;

    Services.Socket.on({
      action: (data) => {
        if (data.RegionSeasonId === activeRegionSeasonId && data.modelName === 'Championship') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  async setActiveRegionSeason({
    RegionSeasonId,
  }: {
    RegionSeasonId: string;
  }) {
    await this.setStateAsync({
      activeRegionSeasonId: RegionSeasonId,
    });

    this.updatePageUrl();

    await this.getData();
  }

  updatePageUrl(): void {
    const {
      main,
    } = this.props;
    const {
      activeRegionSeasonId,
    } = this.state;

    try {
      const regionSeason = getRegionSeasonByRegionSeasonId({
        regions: main.regions,
        RegionSeasonId: activeRegionSeasonId,
      });

      Services.Navigation.replace({
        pathname: `/${regionSeason.Name}`,
      });
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  renderHeader() {
    const {
      main,
      t,
    } = this.props;
    const {
      activeRegionSeasonId,
    } = this.state;

    const region = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId: activeRegionSeasonId,
    });

    return (
      <div className={'bg-white rounded-lg p-5 mb-3'}>
        <div className={'flex flex-row items-center'}>
          <div className={'custom-select mr-3'}>
            <select
              className={'form-control rounded-full btn_primary btn_outlined font-bold'}
              name={'RegionSeasonId'}
              onChange={(e) => {
                const newValue = e.currentTarget.value;

                this.setState(
                  {
                    activeRegionSeasonId: newValue,
                  },
                  () => this.getData(),
                );
              }}
              style={{
                height: 42,
              }}
              value={activeRegionSeasonId}
            >
              {region.RegionSeasons.map((item) => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.Name}
                </option>
              ))}
            </select>
          </div>
          <h2 className={'h3 grow flex flex-row items-center'}>
            {t('Season championships')}
          </h2>
        </div>
      </div>
    );
  }

  renderListsSubSection() {
    const {
      main,
      t,
    } = this.props;
    const {
      activeRegionSeasonId,
      data,
    } = this.state;

    return (data.length > 0 ? (
      <ChampionshipsList
        basePath={getLocalItemBasePath({
          basePath: '/championship',
          regions: main.regions,
          RegionSeasonId: activeRegionSeasonId,
        })}
        data={data}
      />
    ) : (
      <div className={'bg-white rounded-lg p-5'}>
        <NoData label={t('The list is empty')} />
      </div>
    ));
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (loading === true ? (
      <EmptyList items={[[3, 1], [3, 1], [3, 1], [3, 1]]} />
    ) : this.renderListsSubSection());
  }

  render() {
    return (
      <Section
        id={webSections[webPages.championshipsPage].championships}
        noMargin
        transparent
      >
        {this.renderHeader()}
        {this.renderContent()}
      </Section>
    );
  }
}

export default ChampionshipsSectionView;
